import { alpha, styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Card,
  Divider,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ModelTrainingRoundedIcon from '@mui/icons-material/ModelTrainingRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import ArchitectureRoundedIcon from '@mui/icons-material/ArchitectureRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import ScienceRoundedIcon from '@mui/icons-material/ScienceRounded';

const MainCard = styled(Card)(({ theme }) => ({
  borderRadius: 18,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  backgroundColor: theme.palette.background.paper,
  overflow: 'hidden',
}));

const Banner = styled(Box)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.32)}`,
  backgroundColor: alpha(theme.palette.primary.main, 0.06),
  padding: theme.spacing(1.4, 1.6),
}));

const ChoiceCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  borderRadius: 14,
  border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: selected ? alpha(theme.palette.primary.main, 0.04) : theme.palette.background.paper,
  padding: theme.spacing(2),
}));

const ArchitectureCard = styled(Box)(({ theme }) => ({
  borderRadius: 14,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
}));

export default function TrainView() {
  return {
    main: (
      <MainCard>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 3, py: 2.25 }}>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <ModelTrainingRoundedIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: 24, md: 30 } }}>
              Train a Model
            </Typography>
          </Stack>
          <Button variant="outlined" disabled sx={{ borderRadius: 2, textTransform: 'none' }}>
            Start Training
          </Button>
        </Stack>

        <Divider />

        <Box sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Banner>
              <Stack direction="row" justifyContent="space-between" spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                <Stack direction="row" spacing={1.2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <AutoAwesomeRoundedIcon sx={{ color: 'primary.main', mt: { xs: 0.2, sm: 0 } }} />
                  <Typography variant="body2" color="text.secondary">
                    Neural Architecture Search can automatically find the best model for your data. Upgrade your plan to get started.
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>
                    Talk to Sales
                  </Button>
                  <Button size="small" variant="contained" sx={{ borderRadius: 2, textTransform: 'none' }}>
                    Try NAS
                  </Button>
                </Stack>
              </Stack>
            </Banner>

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Select Engine
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8 }}>
                Select how you want to train your model. You can either configure the training yourself or let Roboflow find the optimal model automatically.
              </Typography>
            </Box>

            <Stack spacing={2}>
              <ChoiceCard selected={0}>
                <Stack direction="row" spacing={1.5}>
                  <AutoAwesomeRoundedIcon sx={{ color: 'text.disabled', mt: 0.2 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.disabled' }}>
                      Neural Architecture Search
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                      Let Roboflow automatically design the best models for your data.
                    </Typography>
                    <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.2, color: 'text.secondary' }}>
                      <li><Typography variant="body2">Automated model design</Typography></li>
                      <li><Typography variant="body2">Optimized for your specific dataset</Typography></li>
                      <li><Typography variant="body2">Select speed and accuracy tradeoff after training</Typography></li>
                    </Box>
                  </Box>
                </Stack>
              </ChoiceCard>

              <ChoiceCard selected={1}>
                <Stack direction="row" spacing={1.5}>
                  <TuneRoundedIcon sx={{ color: 'primary.main', mt: 0.2 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Custom Training
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                      Choose your model architecture and configure training parameters.
                    </Typography>
                    <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.2, color: 'text.secondary' }}>
                      <li><Typography variant="body2">Select from existing model architectures</Typography></li>
                      <li><Typography variant="body2">Configure model size and checkpoints</Typography></li>
                    </Box>
                  </Box>
                </Stack>
              </ChoiceCard>
            </Stack>

            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Select Architecture
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.6 }}>
                    Choose the model architecture and size that best fits your use case.
                  </Typography>
                </Box>
                <Button sx={{ textTransform: 'none' }}>Show More</Button>
              </Stack>
            </Box>

            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
              <ArchitectureCard sx={{ flex: 1 }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AvatarShell icon={<ArchitectureRoundedIcon />} />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>Roboflow RF-DETR</Typography>
                    </Stack>
                    <Button size="small" sx={{ textTransform: 'none', color: 'warning.main' }}>Recommended</Button>
                  </Stack>
                  <Box component="ul" sx={{ m: 0, pl: 2.2, color: 'text.secondary' }}>
                    <li><Typography variant="body2">Fast, real-time model. Highest accuracy on COCO</Typography></li>
                    <li><Typography variant="body2">Needs less data and converges earlier. Slower training</Typography></li>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Model Size</Typography>
                  <Select size="small" defaultValue="small" sx={{ borderRadius: 2 }}>
                    <MenuItem value="small">Small</MenuItem>
                  </Select>
                </Stack>
              </ArchitectureCard>

              <ArchitectureCard sx={{ flex: 1 }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AvatarShell icon={<ScienceRoundedIcon />} />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>YOLO26</Typography>
                  </Stack>
                  <Box component="ul" sx={{ m: 0, pl: 2.2, color: 'text.secondary' }}>
                    <li><Typography variant="body2">Latest from Ultralytics</Typography></li>
                    <li><Typography variant="body2">End-to-end NMS-free inference</Typography></li>
                    <li><Typography variant="body2">Faster CPU inference</Typography></li>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Model Size</Typography>
                  <Select size="small" defaultValue="fast" sx={{ borderRadius: 2 }}>
                    <MenuItem value="fast">Fast</MenuItem>
                  </Select>
                </Stack>
              </ArchitectureCard>
            </Stack>

            <Box
              sx={{
                position: 'sticky',
                bottom: 0,
                zIndex: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                bgcolor: 'background.paper',
                px: 2,
                py: 1.5,
              }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between" spacing={1.5}>
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>Select Engine</Button>
                  <Button variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>Select Architecture</Button>
                  <Button variant="outlined" disabled sx={{ borderRadius: 2, textTransform: 'none' }}>Select Version</Button>
                </Stack>
                <Button variant="outlined" disabled startIcon={<BoltRoundedIcon />} sx={{ borderRadius: 2, textTransform: 'none' }}>
                  Start Training
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Box>
      </MainCard>
    ),
    side: null,
  };
}

function AvatarShell({ icon }) {
  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        display: 'grid',
        placeItems: 'center',
        color: 'primary.main',
      }}
    >
      {icon}
    </Box>
  );
}
